/**
 * Shared rules for the post-login "return to where I was" path (`?returnTo=`).
 *
 * Two sides consume this, and they must agree on what a safe destination is —
 * so the rule lives here once rather than drifting between them:
 *   - WRITE side: createAuthLoader builds `?returnTo=<path>` when it bounces a
 *     logged-out user off a protected route.
 *   - READ side: the login component reads `returnTo` after auth and navigates
 *     to it.
 *
 * The destination is attacker-controllable (it's a URL query param), so the
 * read side MUST validate it before navigating. The open-redirect risk: a
 * crafted `?returnTo=https://evil.com` (or the protocol-relative
 * `//evil.com`) would bounce a freshly-authenticated user offsite. The rule:
 * only accept a SAME-ORIGIN in-app path — must start with a single "/" and not
 * "//".
 *
 * The vendor terms page takes the same param for the same reason (a store
 * page sends a store that has not accepted there, and accepting returns it),
 * so it writes with `withReturnTo` and reads with `sanitizeReturnTo` too.
 */

/**
 * `path` carrying `dest` as its `?returnTo=`: the page at `path` sends the
 * person on to `dest` when it is done. `dest` is an in-app path with its
 * search and hash, never an origin.
 */
export const withReturnTo = (path: string, dest: string): string =>
  `${path}?returnTo=${encodeURIComponent(dest)}`;

/**
 * True when `value` is a safe same-origin in-app path: starts with one "/"
 * and not "//" (protocol-relative). Rejects absolute URLs, "//host" forms,
 * empty/nullish values.
 */
export const isSafeReturnTo = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');

/**
 * Returns `value` if it's a safe return-to path, otherwise null. Use on the
 * read side so a malicious or missing param falls through to the default
 * landing (home).
 */
export const sanitizeReturnTo = (value: string | null | undefined): string | null =>
  isSafeReturnTo(value) ? value : null;
